import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { ApiService } from './api.service';
import { DeviceService } from './device.service';
import { AdminUser, SignInData, VerifyOtpData } from '../models/api.models';

const TOKEN_KEY = 'rentwize_admin_token';
const ADMIN_KEY = 'rentwize_admin_user';
const PENDING_EMAIL_KEY = 'rentwize_admin_pending_email';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly device = inject(DeviceService);
  private readonly router = inject(Router);

  readonly admin = signal<AdminUser | null>(this.readAdmin());

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getPendingEmail(): string | null {
    return sessionStorage.getItem(PENDING_EMAIL_KEY);
  }

  setPendingEmail(email: string): void {
    sessionStorage.setItem(PENDING_EMAIL_KEY, email);
  }

  clearPendingEmail(): void {
    sessionStorage.removeItem(PENDING_EMAIL_KEY);
  }

  private persistSession(token: string, admin: AdminUser): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
    this.admin.set(admin);
    this.clearPendingEmail();
    // Keep device row fresh (also registered during signIn body)
    this.api.registerAdminDevice(this.device.getPayload()).subscribe({ error: () => undefined });
  }

  signIn(email: string, password: string): Observable<SignInData> {
    const device = this.device.getPayload();
    return this.api.signIn(email, password, device).pipe(
      tap((data) => {
        if (data?.token && data?.admin) {
          // Direct login (OTP disabled) — also invalidates other device sessions server-side.
          this.persistSession(data.token, data.admin);
          return;
        }
        this.setPendingEmail(data?.email || email);
      })
    );
  }

  verifyOtp(email: string, otp: string): Observable<VerifyOtpData> {
    const device = this.device.getPayload();
    return this.api.verifyOtp(email, otp, device).pipe(
      tap((data) => {
        this.persistSession(data.token, data.admin);
      })
    );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    this.clearPendingEmail();
    this.admin.set(null);
    void this.router.navigate(['/login']);
  }

  private readAdmin(): AdminUser | null {
    try {
      const raw = localStorage.getItem(ADMIN_KEY);
      return raw ? (JSON.parse(raw) as AdminUser) : null;
    } catch {
      return null;
    }
  }
}
